import type { FC } from "react";
import { Box, Container, Typography, Chip, Stack } from "@mui/material";
import { motion } from "framer-motion";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";

const steps = [
  {
    number: "01",
    icon: <AccountCircleOutlinedIcon sx={{ fontSize: 28 }} />,
    title: "Create Account",
    description: "Sign up in minutes with basic details. Quick paperless KYC for seamless onboarding.",
  },
  {
    number: "02",
    icon: <VerifiedUserOutlinedIcon sx={{ fontSize: 28 }} />,
    title: "Verify & Fund",
    description: "Complete KYC and add funds via bank transfer, UPI, IMPS, or RTGS — instantly.",
  },
  {
    number: "03",
    icon: <CurrencyExchangeIcon sx={{ fontSize: 28 }} />,
    title: "Set Exchange",
    description: "Choose destination currency and get real-time competitive rates — no surprises.",
  },
  {
    number: "04",
    icon: <CheckCircleOutlinedIcon sx={{ fontSize: 28 }} />,
    title: "Transfer Complete",
    description: "Funds reach recipient instantly. Track in real-time with full transparency.",
  },
];

export const HomeHowItWorks: FC = () => (
  <Box
    component="section"
    id="how-it-works"
    sx={{
      background: "linear-gradient(180deg, #06040F 0%, #0C0A22 100%)",
      py: { xs: 10, md: 15 },
      position: "relative",
      overflow: "hidden",
    }}
  >
    {/* Subtle dots */}
    <Box sx={{
      position: "absolute", inset: 0, pointerEvents: "none",
      backgroundImage: "radial-gradient(rgba(129, 140, 248, 0.12) 1px, transparent 1px)",
      backgroundSize: "28px 28px",
      zIndex: 0,
    }} />
    {/* Center glow */}
    <Box sx={{
      position: "absolute", top: "30%", left: "50%",
      transform: "translate(-50%, -50%)",
      width: 700, height: 400, borderRadius: "50%",
      background: "radial-gradient(ellipse, rgba(109, 40, 217, 0.18) 0%, transparent 70%)",
      filter: "blur(70px)", zIndex: 0, pointerEvents: "none",
    }} />

    <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: { xs: 7, md: 10 } }}>
        <motion.div initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.55 }}>
          <Chip label="How It Works" sx={{
            mb: 2.5, bgcolor: "rgba(34, 211, 238, 0.12)", color: "#22D3EE",
            border: "1px solid rgba(34, 211, 238, 0.35)", fontWeight: 700, letterSpacing: "0.1em",
          }} />
          <Typography variant="h2" sx={{ color: "#fff", fontWeight: 800, mb: 2, fontSize: { xs: "2rem", md: "3rem" }, lineHeight: 1.15 }}>
            Send money in{" "}
            <Box component="span" sx={{
              background: "linear-gradient(135deg, #818CF8, #22D3EE)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            }}>
              4 simple steps
            </Box>
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.58)", fontSize: "1.05rem", maxWidth: 500, mx: "auto", lineHeight: 1.75 }}>
            Get started in minutes and transfer money globally with complete ease, security, and transparency.
          </Typography>
        </motion.div>
      </Box>

      {/* Steps */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
        gap: 0, position: "relative",
      }}>
        {/* Connector line */}
        <Box sx={{
          position: "absolute",
          top: { md: 56 }, left: { md: "12.5%" }, right: { md: "12.5%" },
          height: 1,
          background: "linear-gradient(90deg, transparent, rgba(129, 140, 248, 0.5), rgba(34, 211, 238, 0.5), transparent)",
          display: { xs: "none", md: "block" }, zIndex: 0,
        }} />

        {steps.map((step, i) => (
          <motion.div key={step.number} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.12 }}>
            <Box sx={{ textAlign: "center", px: 2, py: 3, position: "relative", zIndex: 1 }}>
              <Box sx={{ position: "relative", display: "inline-block", mb: 3 }}>
                <Box sx={{
                  width: 80, height: 80, borderRadius: "50%", mx: "auto",
                  background: "linear-gradient(135deg, rgba(99, 102, 241, 0.25), rgba(34, 211, 238, 0.1))",
                  border: "2px solid rgba(129, 140, 248, 0.45)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#A5B4FC",
                  boxShadow: "0 0 24px rgba(99, 102, 241, 0.2)",
                }}>
                  {step.icon}
                </Box>
                <Box sx={{
                  position: "absolute", top: -6, right: -6,
                  width: 24, height: 24, borderRadius: "50%",
                  background: "linear-gradient(135deg, #4F46E5, #22D3EE)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#fff", fontSize: "0.6rem", fontWeight: 800,
                  boxShadow: "0 2px 10px rgba(99, 102, 241, 0.6)",
                }}>
                  {step.number}
                </Box>
              </Box>
              <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700, mb: 1.5, fontSize: "1rem" }}>
                {step.title}
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.52)", fontSize: "0.85rem", lineHeight: 1.65 }}>
                {step.description}
              </Typography>
            </Box>
          </motion.div>
        ))}
      </Box>

      {/* OTC callout */}
      <motion.div initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.55, delay: 0.3 }}>
        <Box sx={{
          mt: { xs: 8, md: 12 },
          background: "rgba(99, 102, 241, 0.07)",
          border: "1px solid rgba(129, 140, 248, 0.22)",
          borderRadius: 4, p: { xs: 4, md: 6 },
          display: "flex", flexDirection: { xs: "column", md: "row" },
          gap: { xs: 4, md: 6 }, alignItems: { md: "center" },
          boxShadow: "0 0 60px rgba(99, 102, 241, 0.07)",
        }}>
          <Box sx={{ flex: 1 }}>
            <Chip label="OTC Desk" sx={{
              mb: 2.5, bgcolor: "rgba(252, 211, 77, 0.14)", color: "#FCD34D",
              border: "1px solid rgba(252, 211, 77, 0.35)", fontWeight: 700,
            }} />
            <Typography variant="h4" sx={{ color: "#fff", fontWeight: 700, mb: 2, fontSize: { xs: "1.5rem", md: "1.8rem" } }}>
              Need to trade large volumes?
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.6)", lineHeight: 1.78, mb: 3.5, fontSize: "0.93rem" }}>
              Our OTC desk handles trades from ₹10 Lakhs to ₹100 Crores+. Get personalized pricing,
              a dedicated relationship manager, and instant settlement — with full discretion.
            </Typography>
            <Stack spacing={1.5}>
              {[
                "Minimum trade: ₹10 Lakhs",
                "24/7 OTC desk availability",
                "Fiat ↔ Crypto conversions",
                "Same-day settlement guaranteed",
              ].map((pt) => (
                <Stack key={pt} direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: "linear-gradient(135deg, #818CF8, #22D3EE)", flexShrink: 0 }} />
                  <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.875rem" }}>{pt}</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>

          <Box sx={{ flexShrink: 0 }}>
            <Box sx={{
              background: "rgba(252, 211, 77, 0.08)",
              border: "1px solid rgba(252, 211, 77, 0.28)",
              borderRadius: 3, p: { xs: 3, md: 4 },
              textAlign: "center", minWidth: { md: 240 },
              boxShadow: "0 0 30px rgba(252, 211, 77, 0.08)",
            }}>
              <Box sx={{
                width: 50, height: 50, borderRadius: "50%",
                background: "rgba(252, 211, 77, 0.16)",
                display: "flex", alignItems: "center", justifyContent: "center",
                mx: "auto", mb: 2, fontSize: "1.4rem",
              }}>
                📞
              </Box>
              <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.66rem", mb: 1, textTransform: "uppercase", letterSpacing: "0.12em" }}>
                Talk to our OTC Desk
              </Typography>
              <Typography variant="h5" sx={{ color: "#FCD34D", fontWeight: 800, mb: 0.75, letterSpacing: "-0.01em" }}>
                +91 9560107106
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.35)", fontSize: "0.72rem" }}>
                Available 24 × 7
              </Typography>
            </Box>
          </Box>
        </Box>
      </motion.div>
    </Container>
  </Box>
);
